import { RouteObject } from 'react-router-dom';
import ErrorPage, { Page404, WrongNotificationErrorPage, WrongUserErrorPage } from '../error-page';
import onBoardingRoutes from 'src/pages/onboarding/routes';
import personPagesRoutes from 'src/pages/person/routes';
import enterpriseProfileRoutes from 'src/pages/enterprise/routes';
import landingRoutes from 'src/pages/landings/routes';
import publicPersonSimulatorRoutes from 'src/pages/public-simulators/public-person-simulator/routes';
import publicEnterpriseSimulatorRoutes from 'src/pages/public-simulators/public-enterprise-simulator/routes';
import { EnterpriseDraftShortlistProvider } from '../contexts/enterprise-draft-shortlist-context';
import EmailVerificationGuard from '../pages/onboarding/verify-email/verify-email-guard';
import { lazyConReintento } from './lazy-con-reintento';
import { diferida } from './pagina-diferida';

/**
 * Cada pagina viaja en su propio chunk. Lo que NO se difiere, a proposito:
 *
 * - `error-page`, que es la pantalla que sale cuando algo se rompe -- incluido no poder descargar un
 *   chunk. Diferirla seria depender de la red justo cuando la red es el problema.
 * - los guardas y los proveedores de contexto (`EmailVerificationGuard`,
 *   `EnterpriseDraftShortlistProvider`), que envuelven paginas de varias rutas distintas y pesan una
 *   fraccion de lo que envuelven.
 */
const HomeView = lazyConReintento(() => import('src/pages/home'));
const DefaultUserPageView = lazyConReintento(() => import('src/pages/default-user-page-view'));
const ShortLinkRedirect = lazyConReintento(() => import('src/pages/short-link-redirect'));
const StaticPageLayout = lazyConReintento(() => import('src/pages/static/layout'));
const PrivacyPolicyPage = lazyConReintento(() => import('src/shared/components/legal/privacy-policy'));
const UseAgreementPage = lazyConReintento(() => import('src/shared/components/legal/use-agreement'));
const CookiesPolicyPage = lazyConReintento(() => import('src/pages/static/cookies-policy-page'));
const FaqPublicPage = lazyConReintento(() => import('src/pages/static/faq-public-page'));
const SimpleEmailVerificationView = lazyConReintento(() => import('src/pages/onboarding/verify-email/simple-email-verification'));

export const routes: RouteObject[] = [
  {
    path: '/',
    element: diferida(<HomeView />),
    errorElement: <ErrorPage />
  },
  {
    path: '/wrong-notification',
    element: <WrongNotificationErrorPage />
  },
  {
    path: '/wrong-user',
    element: <WrongUserErrorPage />
  },
  ...publicPersonSimulatorRoutes,
  ...publicEnterpriseSimulatorRoutes,
  {
    path: '/go/:shortcode',
    element: diferida(<ShortLinkRedirect />),
    errorElement: <ErrorPage />
  },
  {
    path: '/default-user-page',
    element: diferida(
      <EnterpriseDraftShortlistProvider>
        <DefaultUserPageView />
      </EnterpriseDraftShortlistProvider>
    ),
    errorElement: <ErrorPage />
  },
  {
    path: '/privacy-policy',
    element: diferida(
      <StaticPageLayout>
        <PrivacyPolicyPage />
      </StaticPageLayout>
    ),
    errorElement: <ErrorPage />
  },
  {
    path: '/use-agreement',
    element: diferida(
      <StaticPageLayout>
        <UseAgreementPage />
      </StaticPageLayout>
    ),
    errorElement: <ErrorPage />
  },
  {
    path: '/cookies-policy',
    element: diferida(
      <StaticPageLayout>
        <CookiesPolicyPage />
      </StaticPageLayout>
    ),
    errorElement: <ErrorPage />
  },
  {
    path: '/public-faq',
    element: diferida(
      <StaticPageLayout>
        <FaqPublicPage />
      </StaticPageLayout>
    ),
    errorElement: <ErrorPage />
  },
  {
    path: 'verify-email',
    element: diferida(
      <EmailVerificationGuard mode="redirectIfVerified" successPath={'/default-user-page'} verifyPath={'/default-user-page'}>
        <SimpleEmailVerificationView />
      </EmailVerificationGuard>
    ),
    // Es de primer nivel y no hereda de nadie: sin esto, un chunk que no cargue acaba en la pantalla
    // por defecto de react-router, que esta en ingles y sin marca dentro de una aplicacion en espanol.
    errorElement: <ErrorPage />
  },
  ...onBoardingRoutes,
  ...personPagesRoutes,
  ...enterpriseProfileRoutes,
  ...landingRoutes,
  {
    path: '*',
    element: <Page404 />
  }
];
